import { isAfter, isWithinInterval } from '@if/core/date'
import { EPaymentPlan } from '@if/core/types'
import { formatCurrency, formatUtcIsoDateTime } from '@if/core/utils'
import CalculateIcon from '@mui/icons-material/Calculate'
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { mustShowInterestRate } from '@src/data/creditapp-selectors'
import { getSelectedPlan } from '@src/data/worksheet-selectors'
import useFeatureFlags from '@src/hooks/config-hooks'
import { FinancingProgramConfigs } from '@src/types/Constants'
import { CreditApplication } from '@src/types/CreditApplicationSchema'
import { Merchant } from '@src/types/Merchant'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import PaymentPlanSimulator from './PaymentPlanSimulator'

interface Props {
  creditApp: CreditApplication
  merchant: Merchant
}

const CreditApproved = ({ creditApp, merchant }: Props) => {
  const { t } = useTranslation()
  const { flags } = useFeatureFlags()
  const mustDisplayInterests = mustShowInterestRate(creditApp, merchant, !!flags.UseMerchDBshowRate)
  const [openSimulator, setOpenSimulator] = useState(false)

  const hasActivePaymentPlans = useMemo(() => {
    if (!merchant?.paymentPlans) return false
    const currentDate = new Date()
    const DISTANT_FUTURE = new Date()
    DISTANT_FUTURE.setFullYear(DISTANT_FUTURE.getFullYear() + 100)
    const currentUtcDateFormatted = new Date(formatUtcIsoDateTime(new Date()))
    return merchant.paymentPlans.some((plan) => {
      const planActiveFrom = new Date(plan.activeFrom)
      const planExpiresOn = new Date(plan?.expiresOn ?? DISTANT_FUTURE)
      return (
        isWithinInterval(currentDate, { start: planActiveFrom, end: planExpiresOn }) &&
        isAfter(planExpiresOn, currentUtcDateFormatted)
      )
    })
  }, [merchant?.paymentPlans])

  const selectedPlan = useMemo(() => {
    if (creditApp.merchantPaymentPlanId === null) return undefined
    const planId = (creditApp.worksheet?.paymentPlanId as EPaymentPlan) ?? creditApp.merchantPaymentPlanId
    return getSelectedPlan(merchant, planId)
  }, [creditApp.merchantPaymentPlanId, creditApp.worksheet?.paymentPlanId, merchant])

  const isPromotionSelected = selectedPlan?.promoCode != null

  const selectedPromoRate = selectedPlan?.reducedInterestRate ?? null

  const hasApplicantMismatch = creditApp.applicant.creditAlerts?.hasDoBMismatch
  const hasCoApplicantMismatch = creditApp.coApplicant?.creditAlerts?.hasDoBMismatch === true
  const displayMismatch =
    FinancingProgramConfigs[creditApp.financingProgramId].requiresCreditStepIntervention &&
    (hasApplicantMismatch || hasCoApplicantMismatch)
  const interestRateToDisplay = isPromotionSelected && selectedPromoRate != null
    ? selectedPromoRate
    : creditApp.worksheet?.promotionReducedInterestRate ?? creditApp.finalCreditDecision.interestRate
  const interestRateText = isPromotionSelected
    ? `${t('viewCreditApplication.interestRate')} (${t('common.promotion')})`
    : creditApp.worksheet?.promotionReducedInterestRate != null
      ? t('viewCreditApplication.promotionalInterestRate')
      : t('viewCreditApplication.interestRate')

  return (
    <>
      {flags.UseBuyDownSelectorMerchantDB && hasActivePaymentPlans && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Tooltip title={t('viewCreditApplication.paymentPlanSimulator')}>
            <IconButton size="small" onClick={() => setOpenSimulator(true)}>
              <CalculateIcon />
            </IconButton>
          </Tooltip>
          <PaymentPlanSimulator
            open={openSimulator}
            onClose={() => setOpenSimulator(false)}
            creditApp={creditApp}
            merchant={merchant}
          />
        </Box>
      )}
      {mustDisplayInterests && (
        <div>
          <Typography variant="subtitle1">{interestRateText}</Typography>
          <Typography variant="body1">{`${interestRateToDisplay}%`}</Typography>
        </div>
      )}

      {FinancingProgramConfigs[creditApp.financingProgramId].useMaxPayment && (
        <div>
          <Typography variant="subtitle1">{t('viewCreditApplication.maxMontlyPayment')}</Typography>
          <Typography variant="body1">{formatCurrency(creditApp.finalCreditDecision.maxPmtAmount)}</Typography>
        </div>
      )}

      {FinancingProgramConfigs[creditApp.financingProgramId].displayAmountGrantedCreditTile && (
        <div>
          <Typography variant="subtitle1">{t('viewCreditApplication.amountGranted')}</Typography>
          <Typography variant="body1">{formatCurrency(creditApp.finalCreditDecision.maxAmountFinanced)}</Typography>
        </div>
      )}

      <div>
        <Typography variant="subtitle1">{t('viewCreditApplication.maxTerm')}</Typography>
        <Typography variant="body1">
          {!creditApp.isSinglePayment
            ? `${creditApp.finalCreditDecision.maxTermDuration} ${t('common.months')}`
            : t('viewCreditApplication.isSinglePayment')}
        </Typography>
      </div>

      {displayMismatch && (
        <Typography variant="body2">
          {hasApplicantMismatch && t('viewCreditApplication.applicant')}
          {hasApplicantMismatch && hasCoApplicantMismatch && ', '}
          {hasCoApplicantMismatch && t('viewCreditApplication.coApplicant')}
          {': '}
          {t('viewCreditApplication.dobMismatch')}
        </Typography>
      )}

      {selectedPlan && (
        <div>
          <Stack direction="row" spacing={1} alignItems="baseline">
            <Typography variant="subtitle1">{t('fundingSummary.promotion')}</Typography>
            <Typography variant="body1">{selectedPlan.promoCode}</Typography>
          </Stack>
          <Typography variant="body2">
            {t('viewCreditApplication.promotionalInterestRate')}: {selectedPlan.reducedInterestRate}%
          </Typography>
          <Typography variant="body2">
            {t('viewCreditApplication.promotionalTerm')}: {selectedPlan.reducedRateDurationInMonths} {t('common.months')}
          </Typography>
        </div>
      )}

    </>
  )
}

export default CreditApproved
